import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui';

export interface OneTimeSecretModalProps {
    /** `null` keeps it closed. Present means a secret is on screen right now. */
    secret: OneTimeSecret | null;
    onClose: () => void;
}

export interface OneTimeSecret {
    username: string;
    /** Absent in `invite` mode — Keycloak mailed a set-password link instead. */
    tempPassword?: string;
    /** Shown only on create; a reset has no new sub to report. */
    sub?: string;
    /** Create vs reset — only the wording differs. */
    kind: 'created' | 'reset';
}

/**
 * EAA-1820: frame 66 — "Mật khẩu tạm — hiện đúng một lần".
 *
 * THIS IS A MODAL BECAUSE THE RULE SAYS SO, and the previous shape broke it.
 * The handoff is explicit that the one-time password is "không bao giờ là nội
 * dung tĩnh của trang" — never static page content. It used to render as a
 * `SectionCard` in the page body: a card sits in the normal flow, survives
 * scrolling past it, and is one screenshot or one shoulder away from leaking a
 * live credential that the server does not store and cannot reissue.
 *
 * A modal is the only shape that matches "hiện đúng một lần": it is dismissed
 * deliberately, it cannot be scrolled past, and once closed the caller drops the
 * value and there is no path that renders it again — `onClose` clears the state
 * that held it, and nothing refetches it, because the server never kept it.
 *
 * The value is therefore NEVER put in route state, storage, or a toast; it lives
 * only in the caller's component state for as long as this modal is open.
 */
export function OneTimeSecretModal({ secret, onClose }: OneTimeSecretModalProps) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    // EAA-1820 (frame 71): the footer checkbox — operator must acknowledge
    // before "Xong" enables, matching the handoff's saved-password gate.
    const [savedAck, setSavedAck] = useState(false);

    async function copy(value: string) {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard denied (insecure origin, permission) — the value is on
            // screen and selectable, so this is not worth an error state.
        }
    }

    function handleClose() {
        setCopied(false);
        setSavedAck(false);
        onClose();
    }

    const isReset = secret?.kind === 'reset';
    const title = isReset
        ? t('console.accounts.secretModal.resetTitle')
        : t('console.accounts.secretModal.createdTitle');
    const hasPassword = Boolean(secret?.tempPassword);
    const canClose = !hasPassword || savedAck;

    return (
        <Modal
            open={secret !== null}
            onCancel={handleClose}
            title={title}
            width={520}
            footer={
                <div className="acc-secret__footer">
                    {hasPassword && (
                        <label className="acc-secret__saved-ack">
                            <input
                                type="checkbox"
                                checked={savedAck}
                                onChange={(e) => setSavedAck(e.target.checked)}
                            />
                            {t('console.accounts.secretModal.savedAckLabel')}
                        </label>
                    )}
                    <button
                        type="button"
                        className="portal-btn portal-btn-primary"
                        onClick={handleClose}
                        disabled={!canClose}
                    >
                        {t('console.accounts.secretModal.savedButton')}
                    </button>
                </div>
            }
        >
            <div className="acc-secret">
                <p className="acc-secret__lead">
                    {isReset
                        ? t('console.accounts.secretModal.resetLead')
                        : t('console.accounts.secretModal.createdLead')}
                </p>

                <dl className="acc-secret__rows">
                    <div className="acc-secret__row">
                        <dt>{t('console.accounts.secretModal.usernameLabel')}</dt>
                        <dd>
                            <code>{secret?.username}</code>
                        </dd>
                    </div>
                    {secret?.sub && (
                        <div className="acc-secret__row">
                            <dt>{t('console.accounts.secretModal.subLabel')}</dt>
                            <dd>
                                <code>{secret.sub}</code>
                            </dd>
                        </div>
                    )}
                    {hasPassword && (
                        <div className="acc-secret__row acc-secret__row--secret">
                            <dt>{t('console.accounts.secretModal.tempPasswordLabel')}</dt>
                            <dd>
                                <code className="acc-secret__value">{secret?.tempPassword}</code>
                                <button
                                    type="button"
                                    className="portal-btn"
                                    onClick={() => copy(secret?.tempPassword as string)}
                                >
                                    {copied
                                        ? t('console.accounts.secretModal.copiedButton')
                                        : t('console.accounts.secretModal.copyButton')}
                                </button>
                            </dd>
                        </div>
                    )}
                </dl>

                {hasPassword ? (
                    /* Required copy, verbatim from the frame — it is the whole
                       reason the modal cannot be reopened. */
                    <p className="acc-secret__warn" role="alert">
                        {t('console.accounts.secretModal.closeWarnsLoss')}
                    </p>
                ) : (
                    <p className="acc-secret__warn">
                        {t('console.accounts.secretModal.inviteSentNote')}
                    </p>
                )}
            </div>
        </Modal>
    );
}

export default OneTimeSecretModal;
